import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { Check as CheckIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import TimeSinceDate from 'components/util/TimeSinceDate';
import { FormattedMessage } from 'react-intl';
import { ComponentProps } from 'react';
import { AutomatedNotification } from 'types/notifications';
import { cn } from 'utils/cn';
import FormattedNotifcationText from './FormattedNotificationText';

interface Props {
    notification: AutomatedNotification;
    handleRead: (id: string | number | 'all', isMarkingRead?: boolean) => Promise<void>;
    handleArchive: (id: string | number | 'all') => Promise<void>;
    areButtonsDisabled: boolean;
}

function NotificationItem({ notification, handleArchive, handleRead, areButtonsDisabled }: Props) {
    const isUnread = !notification?.readAt;

    return (
        <li className="border-divider relative flex flex-col gap-4 border-b py-4 pr-6 pl-10">
            <span
                className={cn('bg-error absolute top-6 left-6 h-2 w-2 rounded-full transition-opacity duration-150', {
                    'opacity-0': !isUnread,
                })}
            />
            <div className="flex flex-col gap-1">
                {notification?.isImportant ? (
                    <div className="self-start">
                        <StatusChip
                            type="warning"
                            label={<FormattedMessage id="notifications.important" values={{ count: 0 }} />}
                        />
                    </div>
                ) : null}
                <FormattedNotifcationText notification={notification} />
                <TimeSinceDate date={notification.createdAt} />
            </div>
            <div className="flex flex-row gap-1">
                <ItemActionButton
                    onClick={() => {
                        void handleRead(notification.id, isUnread);
                    }}
                    disabled={areButtonsDisabled}
                >
                    <span
                        className={cn(
                            'inline-flex shrink-0 items-center overflow-hidden transition-[width,opacity] duration-150',
                            isUnread ? 'w-4 opacity-100' : 'w-0 opacity-0'
                        )}
                    >
                        <CheckIcon
                            fontSize="inherit"
                            className={cn(
                                cn('transition-[scale] duration-150', isUnread ? 'scale-100' : 'scale-75'),
                                'text-success'
                            )}
                        />
                    </span>
                    <FormattedMessage
                        id={notification?.readAt ? 'notifications.mark-as-unread' : 'notifications.mark-as-read'}
                    />
                </ItemActionButton>
                <ItemActionButton
                    onClick={() => {
                        void handleArchive(notification.id);
                    }}
                    disabled={areButtonsDisabled}
                >
                    <FormattedMessage id="notifications.archive" />
                </ItemActionButton>
            </div>
        </li>
    );
}

/** The small outlined buttons under a notification. */
export const ItemActionButton = (props: Omit<ComponentProps<typeof Button>, 'className'>) => (
    <Button {...props} {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'gap-0 px-2.5 font-semibold')} />
);

export default NotificationItem;
